import { Card, Input } from "antd";
import { HomeLayout } from "../../layouts";
import { useState, useEffect } from "react";
import { wordsFetcher } from "../../services/api";
import { Word } from "../../types/words";
import { CriteriaOption } from "../../types/task";
import { useMessageContext } from "../../providers";

import styles from "./WhiteList.module.css";
import WordsList from "./WordsList/WordsList";
import CreateWordForm from "./CreateWord/CreateWordForm";

const { Search } = Input;

const WhiteList = () => {
  const [words, setWords] = useState<Word[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [criteriaFilter, setCriteriaFilter] = useState<string[]>([]);
  const { sendMessage } = useMessageContext();

  const loadWords = async () => {
    try {
      setLoading(true);
      const response = await wordsFetcher.listWords();
      setWords(response.data);
    } catch (error) {
      console.error("Error loading words:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleWordUpdate = async (updatedWord: Word) => {
    try {
      setLoading(true);
      
      const response = await wordsFetcher.updateWord(updatedWord.id, {
        word: updatedWord.word,
        additional_info: updatedWord.additional_info,
        criteria: updatedWord.criteria
      });
            
      if (response.data) {
        setWords(prevWords => 
          prevWords.map(word => 
            word.id === updatedWord.id ? response.data : word
          )
        );
        sendMessage(
          `La paraula "${updatedWord.word}" s'ha actualitzat correctament`,
          "success"
        );
      }
    } catch (error) {
      console.error("Error actualitzant la paraula:", error);
      sendMessage(
        `Hi ha hagut un error actualitzant la paraula "${updatedWord.word}". Si us plau, torna a intentar-ho més tard`,
        "error",
        3
      );
    } finally {
      setLoading(false);
    }
  };

  const handleWordDelete = async (wordKey: string) => {
    try {
      setLoading(true);
      
      await wordsFetcher.deleteWord(wordKey);
      
      const wordToDelete = words.find(word => word.id === wordKey);
      const wordName = wordToDelete?.word || "la paraula";
      
      setWords(prevWords => 
        prevWords.filter(word => word.id !== wordKey)
      );
      
      sendMessage(
        `La paraula "${wordName}" s'ha eliminat correctament`,
        "success"
      );
    } catch (error) {
      console.error("Error eliminant la paraula:", error);
      sendMessage(
        `Hi ha hagut un error eliminant la paraula. Si us plau, torna a intentar-ho més tard`,
        "error",
        3
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWords();
  }, []);

  // Aplicar ambos filtros: criterios y búsqueda
  const visibleWords = words.filter(word => {
    // Filtro por criterios
    if (criteriaFilter.length > 0) {
      if (!word.criteria || word.criteria.length === 0) return false;
      const hasMatchingCriteria = criteriaFilter.some(selectedCriteri => 
        word.criteria?.includes(selectedCriteri as CriteriaOption)
      );
      if (!hasMatchingCriteria) return false;
    }
    
    // Filtro por búsqueda
    if (searchTerm) {
      const matchesSearch = word.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
        word.additional_info?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
    }
    
    return true;
  });

  return (
    <HomeLayout>
      <main className={styles.main}>
        <div className={styles.homeContainer}>
          <div className={styles.homeHeader}>
            <div className={styles.homeHeaderTitle}>
              <h1>Llista blanca.</h1>
              <h3 className={styles.subtitle}>Configura el conjunt de paraules que cal excloure dels criteris d’anonimització establerts.</h3>
            </div>
          </div>
          <div className={styles.homeContent}>
            <Card>
              <CreateWordForm onWordCreated={loadWords} existingWords={words} />
            </Card>
            <div className={styles.homeContentList}>
              <div className={styles.homeContentHeader}>
                <span>{visibleWords.length} {visibleWords.length === 1 ? "paraula" : "paraules"}</span>
                <Search 
                  className={styles.search} 
                  placeholder="Cercar" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <WordsList 
                words={visibleWords} 
                loading={loading} 
                onWordUpdate={handleWordUpdate}
                onWordDelete={handleWordDelete}
                criteriaFilter={criteriaFilter}
                onCriteriaFilterChange={setCriteriaFilter}
              />
            </div>
          </div>
        </div>
      </main>
    </HomeLayout>
  );
};

export default WhiteList;
